import { bbox } from "@turf/turf";
import type {
  Feature,
  FeatureCollection,
  GeoJsonProperties,
  Geometry,
} from "geojson";

type LngLatBbox = [number, number, number, number];

/** Structural type so turf and geojson features both type-check. */
type FeatureLike = {
  type?: string;
  geometry?: Geometry | null;
  properties?: GeoJsonProperties;
};

type FeatureCollectionLike = {
  type?: string;
  features?: FeatureLike[];
};

export function featureHasValidGeometry(
  feature: FeatureLike | null | undefined
): feature is Feature {
  return Boolean(feature?.geometry?.coordinates);
}

/** Drop features that would crash turf bbox/coordEach. */
export function sanitizeFeatureCollectionForBbox(
  collection: FeatureCollectionLike
): FeatureCollection {
  const features = (collection.features ?? []).filter(featureHasValidGeometry);
  return {
    type: "FeatureCollection",
    features,
  };
}

export function getFeatureCollectionBbox(
  collection: FeatureCollectionLike | null | undefined
): LngLatBbox | null {
  if (!collection?.features?.length) {
    return null;
  }

  const sanitized = sanitizeFeatureCollectionForBbox(collection);
  if (sanitized.features.length === 0) {
    return null;
  }

  try {
    const bounds = bbox(sanitized) as LngLatBbox;
    return bounds;
  } catch {
    return null;
  }
}

export function isValidLngLatBbox(bounds: LngLatBbox): boolean {
  const [minLng, minLat, maxLng, maxLat] = bounds;
  return (
    Number.isFinite(minLng) &&
    Number.isFinite(minLat) &&
    Number.isFinite(maxLng) &&
    Number.isFinite(maxLat) &&
    minLng < maxLng &&
    minLat < maxLat
  );
}
